/**
 * Shopper Intent Event Definitions
 *
 * Centralized definitions for shopper intent events dispatched by the
 * Shopify extension and consumed by the chat widget.
 */

/**
 * Shopper Intent Event Names
 * These match the CustomEvent names dispatched by the Shopify extension
 */
export const SHOPPER_INTENT_EVENTS = {
  VARIANT_INDECISION: "redo:shopper-intent:variant-indecision",
  PRODUCT_COMPARISON: "redo:shopper-intent:product-comparison",
  IMAGE_BROWSING: "redo:shopper-intent:image-browsing",
} as const;

export type ShopperIntentEventName =
  (typeof SHOPPER_INTENT_EVENTS)[keyof typeof SHOPPER_INTENT_EVENTS];

/**
 * Variant Indecision Event Data
 *
 * Fires when: 5+ variant swaps within 30 seconds, with oscillation
 * (revisiting previous selections)
 */
export interface VariantIndecisionEventData {
  swapCount: number;
  uniqueVariants: number;
  variants: string[];
  variantCounts: Record<string, number>;
  windowMs: number;
  optionTypes: Array<"color" | "size" | "other">;
}

/**
 * Product Comparison Event Data
 *
 * Fires when: 2+ products are each viewed 3+ times within 2 minutes
 */
export interface ProductComparisonEventData {
  products: string[];
  viewCounts: Record<string, number>;
  windowMs: number;
}

/**
 * Image Browsing Event Data
 *
 * Fires when: 6+ image views within 60 seconds
 */
export interface ImageBrowsingEventData {
  viewCount: number;
  windowMs: number;
  productId: string | null;
}

/**
 * Discriminated union of all shopper intent event details
 */
export type ShopperIntentEventDetail =
  | {
      type: typeof SHOPPER_INTENT_EVENTS.VARIANT_INDECISION;
      data: VariantIndecisionEventData;
    }
  | {
      type: typeof SHOPPER_INTENT_EVENTS.PRODUCT_COMPARISON;
      data: ProductComparisonEventData;
    }
  | {
      type: typeof SHOPPER_INTENT_EVENTS.IMAGE_BROWSING;
      data: ImageBrowsingEventData;
    };
